/* eslint-disable @typescript-eslint/no-explicit-any */
import { Tag as TagType } from '@eigtech/resource-tagging-types'
import { Corn } from '@eigtech/shared-corn'
import { ContextualCan } from '@eigtech/ui-shared-auth'
import { mostReadable, stringToColor } from '@eigtech/ui-shared-color'
import { getLog } from '@eigtech/ui-shared-logging'
import { useGetTagsByResource, useRemoveTagFromResource } from '@eigtech/ui-shared-tags'
import { Skeleton } from '@eigtech/ui/components/skeleton'
import { toast } from '@eigtech/ui/components/sonner'
import { Tag, TagButton, TagLabel, TagProps } from '@eigtech/ui/components/tag'
import { Wrap, WrapItem } from '@eigtech/ui/components/wrap'
import { cn } from '@eigtech/ui/lib/utils'
import { useEffect } from 'react'

const log = getLog()

export type TagsListProps = {
  ref?: React.Ref<HTMLDivElement>
  className?: string
  resourceCorn: Corn
  onTagClick?: (tag: TagType) => any
}

export function TagsList({ className, resourceCorn, onTagClick, ...props }: TagsListProps) {
  const { data: tags = [], isPending, isError } = useGetTagsByResource({ resourceCorn })

  useEffect(() => {
    if (isError) {
      toast.error('Could not retrieve tags for resource')
    }
  }, [isError])

  return (
    <Wrap className={cn('gap-2', className)} {...props}>
      {isPending ? (
        <>
          <WrapItem>
            <Skeleton className="h-6 w-20" />
          </WrapItem>
          <WrapItem>
            <Skeleton className="h-6 w-20" />
          </WrapItem>
          <WrapItem>
            <Skeleton className="h-6 w-20" />
          </WrapItem>
        </>
      ) : (
        tags.map((tag) => (
          <WrapItem key={`${tag.key}:${tag.value}`}>
            <TagItem tag={tag} onTagClick={onTagClick} />
          </WrapItem>
        ))
      )}
    </Wrap>
  )
}

export type TagItemProps = TagProps & { tag: TagType; onTagClick?: (tag: TagType) => any }

export function TagItem({ tag, onTagClick, ...props }: TagItemProps) {
  const { mutateAsync: removeTag, isPending } = useRemoveTagFromResource()

  async function handleRemoveTag() {
    const promise = removeTag(tag)

    toast.promise(promise, {
      loading: 'Removing tag...',
      error: (e) => {
        log.error('error removing tag', { error: e })
        return 'Something went wrong trying to remove this tag.'
      },
      success: () => 'Successfully removed tag!',
    })
  }

  const label = () => {
    let label = tag.key
    if (tag.value !== 'EMPTY_VALUE') {
      label = `${label}:${tag.value}`
    }
    return label
  }

  const color = tag.color || stringToColor(tag.key)
  const fontColor = mostReadable(color, ['#fff', '#000'])?.toHexString()

  return (
    <Tag
      style={
        {
          '--color-info-accent': `${color}`,
          '--color-info-foreground': fontColor,
        } as React.CSSProperties
      }
      variant="info"
      {...props}
    >
      <TagLabel className={cn(onTagClick && 'cursor-pointer')} onClick={() => onTagClick?.(tag)}>
        {label()}
      </TagLabel>

      <ContextualCan I="delete" a="tag">
        <TagButton disabled={isPending} onClick={handleRemoveTag} />
      </ContextualCan>
    </Tag>
  )
}
